import { useCallback, useEffect, useRef, useState } from 'react';
import { Footer } from '@/components/layout/Footer';
import { Header } from '@/components/layout/Header';
import { BackToTop } from '@/components/ui/BackToTop';
import { SECTION_TITLES, SITE_NAME } from '@/data/sections';
import { SEED_GALLERY, SEED_QUESTIONS } from '@/data/seeds';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useHashRoute } from '@/hooks/useHashRoute';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { usePersistentCollection } from '@/hooks/usePersistentCollection';
import { useTheme } from '@/hooks/useTheme';
import { scrollToTop } from '@/lib/scroll';
import { STORAGE_KEYS } from '@/lib/storage';
import { parseGalleryItem, parseQuestion } from '@/lib/validation';
import { Contact } from '@/sections/Contact';
import { Gallery } from '@/sections/Gallery';
import { Home } from '@/sections/Home';
import { Qa } from '@/sections/Qa';
import { Resources } from '@/sections/Resources';
import { Tools } from '@/sections/Tools';
import type { GalleryItem, Question, Reply, SectionId } from '@/types';
import styles from './App.module.css';

const VIEW_TITLE_ID = 'view-title';

export function App() {
  const { section, navigate } = useHashRoute();
  const { isDark, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  // The toggle and panel are display:none above this width. Left open across
  // the breakpoint they stay mounted and announced, and Escape would move
  // focus to a hidden button — which lands on <body> instead.
  const isWide = useMediaQuery('(min-width: 860px)');
  const mainRef = useRef<HTMLElement>(null);
  // Comparing against the previously rendered section — rather than flagging
  // "the first effect run" — keeps the mount case quiet without breaking under
  // StrictMode, whose mount/unmount/mount cycle runs the effect twice and would
  // otherwise steal focus on first paint.
  const previousSection = useRef(section);

  useDocumentTitle(`${SECTION_TITLES[section]} — ${SITE_NAME}`);

  const questions = usePersistentCollection<Question>(
    STORAGE_KEYS.questions,
    SEED_QUESTIONS,
    parseQuestion,
  );

  const gallery = usePersistentCollection<GalleryItem>(
    STORAGE_KEYS.gallery,
    SEED_GALLERY,
    parseGalleryItem,
  );

  // On navigation, move to the top and hand focus to the new view so keyboard
  // and screen-reader users land on the content they just asked for.
  useEffect(() => {
    setMenuOpen(false);

    if (previousSection.current === section) return;
    previousSection.current = section;

    scrollToTop();
    mainRef.current?.focus({ preventScroll: true });
  }, [section]);

  const handleNavigate = useCallback(
    (next: SectionId) => {
      const sameSection = next === section;

      navigate(next);
      setMenuOpen(false);

      // Re-selecting the current section leaves `section` unchanged, so the
      // effect above never runs. Do the work here instead — otherwise the menu
      // link that had focus unmounts and focus drops to <body>.
      if (sameSection) {
        scrollToTop();
        mainRef.current?.focus({ preventScroll: true });
      }
    },
    [navigate, section],
  );

  useEffect(() => {
    if (isWide) setMenuOpen(false);
  }, [isWide]);

  const handleReply = useCallback(
    (questionId: string, reply: Reply) => {
      questions.update(
        (question) => question.id === questionId,
        (question) => ({ ...question, replies: [...question.replies, reply] }),
      );
    },
    [questions],
  );

  const views: Record<SectionId, JSX.Element> = {
    home: <Home onNavigate={handleNavigate} titleId={VIEW_TITLE_ID} />,
    tools: <Tools titleId={VIEW_TITLE_ID} />,
    qa: (
      <Qa
        titleId={VIEW_TITLE_ID}
        questions={questions.items}
        onAsk={questions.add}
        onReply={handleReply}
      />
    ),
    resources: <Resources titleId={VIEW_TITLE_ID} />,
    gallery: (
      <Gallery
        titleId={VIEW_TITLE_ID}
        items={gallery.items}
        onAdd={gallery.add}
        onRemove={(id) => gallery.remove((item) => item.id === id)}
        onRestore={gallery.insertAt}
      />
    ),
    contact: <Contact titleId={VIEW_TITLE_ID} />,
  };

  return (
    <div className={styles.page}>
      <a className="skip-link" href="#main">
        تخطَّ إلى المحتوى
      </a>

      <Header
        section={section}
        onNavigate={handleNavigate}
        isDark={isDark}
        onToggleTheme={toggleTheme}
        menuOpen={menuOpen}
        onToggleMenu={() => setMenuOpen((open) => !open)}
        onCloseMenu={() => setMenuOpen(false)}
      />

      {/* Labelling the landmark itself gives it a name that changes with the
          view; `aria-labelledby` on the generic wrapper below would be ignored. */}
      <main
        className={styles.main}
        id="main"
        ref={mainRef}
        tabIndex={-1}
        aria-labelledby={VIEW_TITLE_ID}
      >
        {/* Re-keying on `section` restarts the enter animation per view. */}
        <div className={styles.view} key={section}>
          {views[section]}
        </div>
      </main>

      <Footer section={section} onNavigate={handleNavigate} />

      <BackToTop />
    </div>
  );
}
